/* eslint-disable max-lines */
import { trpcNoAuth } from '@prishapolicy/shared/trpc';
import { useInsuranceForDoctorsFormType } from '@prishapolicy/w3/ui';
import { useRouter } from 'next/router';
import comprehensiveImage from './comprehensive.png';
import standaloneODImage from './saod.png';
import thirdPartyImage from './third-party.png';
import individual from './individual.png';
import family from './family.png';
import owned from './owned.png';
import rented from './rented.png';
import * as Yup from 'yup';
import { object } from 'yup';
import {
  BoxSelect,
  Button,
  DatePicker,
  Form,
  SearchWithRadioButton,
  showToast,
  TextInput,
} from '@prishapolicy/shared/ui';
import { OptionsArray, regex } from '@prishapolicy/backend-core/masters';

type MotorCoverType = 'standalone-od' | 'comprehensive' | 'third-party';

type HealthInsuranceType = 'individual-insurance' | 'family-insurance';

type PropertyType = 'owned' | 'rented';

export const CoverageDetailsForm = () => {
  const { setFormInfo, setMoreOffersForm, formInfo } = useInsuranceForDoctorsFormType();
  const updateLeadMutation = trpcNoAuth['lead-management']['lead']['update-lead-for-doctors'].useMutation();
  const router = useRouter();
  const { productType } = router.query;

  const typeOfCoverImageMapping: Record<MotorCoverType, any> = {
    'standalone-od': standaloneODImage,
    comprehensive: comprehensiveImage,
    'third-party': thirdPartyImage,
  };

  const optionsArray = ['comprehensive', 'standalone-od', 'third-party'].map((make) => ({
    value: make,
    image: typeOfCoverImageMapping[make as MotorCoverType],
    mainText: make === 'comprehensive' ? 'Comprehensive' : make === 'standalone-od' ? 'Standalone OD' : 'Third Party',
  }));

  const typeOfInsuranceImageMapping: Record<HealthInsuranceType, any> = {
    'individual-insurance': individual,
    'family-insurance': family,
  };

  const InsuranceTypesOptionsArray = ['individual-insurance', 'family-insurance'].map((option) => ({
    value: option,
    image: typeOfInsuranceImageMapping[option as HealthInsuranceType],
    mainText: option === 'individual-insurance' ? 'Individual Insurance' : 'Family Insurance',
  }));

  const typeOfPropertyImageMapping: Record<PropertyType, any> = {
    owned: owned,
    rented: rented,
  };
  const PropertyTypeOptionsArray = ['owned', 'rented'].map((option) => ({
    value: option,
    image: typeOfPropertyImageMapping[option as PropertyType],
    mainText: option === 'owned' ? 'Owned' : 'Rented',
  }));

  const NumberOfBedsOptionsArray = [
    { value: '0-10', mainText: '0-10' },
    { value: '11-20', mainText: '11-20' },
    { value: '21-30', mainText: '21-30' },
    { value: '31-40', mainText: '31-40' },
    { value: '41-50', mainText: '41-50' },
    { value: '51-75', mainText: '51-75' },
    { value: '75+', mainText: '75+' },
  ];

  const SumInsuredOptionsArray = [
    { value: 'Upto 5 Lakh', mainText: 'Upto 5 Lakh' },
    { value: '5 Lakh to 10 Lakh', mainText: '5 Lakh to 10 Lakh' },
    { value: '10 Lakh to 12 Lakh', mainText: '10 Lakh to 12 Lakh' },
    { value: '12 Lakh to 15 Lakh', mainText: '12 Lakh to 15 Lakh' },
    { value: '15 Lakh +', mainText: '15 Lakh +' },
  ];

  const formInitialValues = (() => {
    switch (productType) {
      case 'motor-insurance':
        return {
          vehicleRegistrationNumber: formInfo.vehicleRegistrationNumber || null,
          vehicleCoverType: formInfo.vehicleCoverType || '',
          policyExpiryDate: formInfo.policyExpiryDate || null,
        };
      case 'professional-indemnity-for-medical-setup':
        return {
          numberOfBedsRange: formInfo.numberOfBedsRange || '',
        };
      case 'professional-indemnity-for-doctors':
        return {
          selectedSpeciality: formInfo.selectedSpeciality || null,
        };
      case 'health-insurance':
        return {
          insuranceType: formInfo.insuranceType || '',
          sumInsuredRange: formInfo.sumInsuredRange || '',
        };
      case 'home-insurance': {
        return {
          pincode: formInfo.pincode || null,
          propertyType: formInfo.propertyType || '',
          sumInsuredRange: formInfo.sumInsuredRange || '',
        };
      }
      case 'cyber-insurance':
        return {
          sumInsuredRange: formInfo.sumInsuredRange || '',
        };
      default:
        return {};
    }
  })();

  const validationSchema = object().shape(
    (() => {
      switch (productType) {
        case 'motor-insurance':
          return {
            vehicleRegistrationNumber: Yup.string()
              .required('Vehicle Registration Number is required')
              .matches(regex.vehicleRegistrationNumber, 'Invalid Vehicle Registration Number'),
            vehicleCoverType: Yup.string()
              .required('Vehicle Cover Type is required')
              .typeError('Vehicle Cover Type is required'),
            policyExpiryDate: Yup.date()
              .required('Policy Expiry Date is required')
              .typeError('Policy Expiry Date is required'),
          };
        case 'professional-indemnity-for-medical-setup':
          return {
            numberOfBedsRange: Yup.string()
              .required('Number of Beds Range is required')
              .typeError('Number of Beds Range is required'),
          };
        case 'professional-indemnity-for-doctors':
          return {
            selectedSpeciality: Yup.string().required('Speciality is required').typeError('Speciality is required'),
          };
        case 'health-insurance':
          return {
            insuranceType: Yup.string().required('Insurance Type is required').typeError('Insurance Type is required'),
            sumInsuredRange: Yup.string().required('Sum Insured is required').typeError('Sum Insured is Required'),
          };
        case 'home-insurance':
          return {
            pincode: Yup.number().required('Pincode is required').typeError('Pincode is required'),
            propertyType: Yup.string().required('Property Type is required').typeError('Property Type is required'),
            sumInsuredRange: Yup.string().required('Sum Insured is required').typeError('Sum Insured is Required'),
          };
        case 'cyber-insurance':
          return {
            sumInsuredRange: Yup.string().required('Sum Insured is required').typeError('Sum Insured is Required'),
          };
        default:
          return {};
      }
    })(),
  );

  const formContent = (() => {
    switch (productType) {
      case 'motor-insurance':
        return (
          <>
            <div className="text-title-6 font-bold">Motor Insurance</div>
            <div className="scrollbar-hide mt-2 h-[500px] max-h-[500px] overflow-auto whitespace-nowrap bg-white px-1">
              <TextInput name="vehicleRegistrationNumber" placeholder="Vehicle Registration Number" label="" />
              <BoxSelect
                name="vehicleCoverType"
                label="Type of Cover"
                perRow={3}
                options={[{ options: optionsArray }]}
                customBoxStyle={true}
              />
              <DatePicker name="policyExpiryDate" />
            </div>
          </>
        );
      case 'professional-indemnity-for-medical-setup':
        return (
          <>
            <div className="text-title-6 font-bold">Professional Indemnity for Medical Setup</div>
            <div className="scrollbar-hide mt-2 h-[500px] max-h-[500px] overflow-auto whitespace-nowrap bg-white px-1">
              <BoxSelect
                name="numberOfBedsRange"
                label="Select Number of Beds"
                perRow={1}
                options={[{ options: NumberOfBedsOptionsArray }]}
                customBoxStyle={true}
              />
            </div>
          </>
        );
      case 'professional-indemnity-for-doctors':
        return (
          <>
            <div className="text-title-6 font-bold">Professional Indemnity for Doctors</div>
            <div className="scrollbar-hide mt-2 h-[500px] max-h-[500px] overflow-auto whitespace-nowrap bg-white px-1">
              <SearchWithRadioButton
                name="selectedSpeciality"
                options={OptionsArray}
                placeholder="Search a speciality"
                showOther={true}
              />
            </div>
          </>
        );
      case 'health-insurance':
        return (
          <>
            <div className="text-title-6 font-bold">Health Insurance</div>
            <div className="scrollbar-hide mt-2 h-[500px] max-h-[500px] overflow-auto whitespace-nowrap bg-white px-1">
              <BoxSelect
                name="insuranceType"
                label="Insurance Type"
                perRow={2}
                options={[{ options: InsuranceTypesOptionsArray }]}
                customBoxStyle={true}
              />
              <BoxSelect
                name="sumInsuredRange"
                label="Select Sum Insured"
                perRow={1}
                options={[{ options: SumInsuredOptionsArray }]}
                customBoxStyle={true}
              />
            </div>
          </>
        );
      case 'home-insurance':
        return (
          <>
            <div className="text-title-6 font-bold">Home Insurance</div>
            <div className="scrollbar-hide mt-2 h-[500px] max-h-[500px] overflow-auto whitespace-nowrap bg-white px-1">
              <TextInput name="pincode" placeholder="Pincode" label="" />
              <BoxSelect
                name="propertyType"
                label="Property Type"
                perRow={2}
                options={[{ options: PropertyTypeOptionsArray }]}
                customBoxStyle={true}
              />
              <BoxSelect
                name="sumInsuredRange"
                label="Select Sum Insured"
                perRow={1}
                options={[{ options: SumInsuredOptionsArray }]}
                customBoxStyle={true}
              />
            </div>
          </>
        );

      case 'cyber-insurance':
        return (
          <>
            <div className="text-title-6 font-bold">Cyber Insurance</div>
            <div className="scrollbar-hide mt-2 h-[500px] max-h-[500px] overflow-auto whitespace-nowrap bg-white px-1">
              <BoxSelect
                name="sumInsuredRange"
                label="Select Sum Insured"
                perRow={1}
                options={[{ options: SumInsuredOptionsArray }]}
                customBoxStyle={true}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  })();

  return (
    <Form
      initialValues={formInitialValues}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        console.log('values here', values);
        console.log('pin', values.pincode);
        setFormInfo({
          vehicleRegistrationNumber: values.vehicleRegistrationNumber,
          vehicleCoverType: values.vehicleCoverType,
          policyExpiryDate: values.policyExpiryDate,

          numberOfBedsRange: values.numberOfBedsRange,

          selectedSpeciality: values.selectedSpeciality,

          insuranceType: values.insuranceType,
          sumInsuredRange: values.sumInsuredRange,

          pincode: values.pincode ? Number(values.pincode) : null,
          propertyType: values.propertyType,
        });

        await updateLeadMutation.mutateAsync({
          leadId: formInfo.leadId,

          vehicleRegistrationNumber: values.vehicleRegistrationNumber,
          vehicleCoverType: values.vehicleCoverType,
          policyExpiryDate: values.policyExpiryDate,

          numberOfBedsRange: values.numberOfBedsRange,

          selectedSpeciality: values.selectedSpeciality,

          insuranceType: values.insuranceType,
          sumInsuredRange: values.sumInsuredRange,

          pincode: values.pincode ? Number(values.pincode) : null,
          propertyType: values.propertyType,
        });

        setMoreOffersForm();

        showToast({
          title: 'Form successfully filled!',
          variant: 'success',
        });
      }}
      className="grid  rounded-lg bg-white px-5 pb-5"
    >
      {formContent}
      <div className="sticky bottom-0 mt-3 rounded-lg bg-white">
        <Button
          text="Save & Next"
          rightIcon="arrowRight"
          className="w-full"
          type="submit"
          disabled={updateLeadMutation.isPending}
        />
      </div>
    </Form>
  );
};
